import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

export interface CartItem {
  id: string;
  name: string;
  subtitle: string;
  brand: string;
  productImage: string;
  price: number;
  quantity: number;
  version?: string;
  availableVersions?: string[];
  versionPrices?: Record<string, number>;
}

export type ShippingLocation = 'ramallah' | 'west-bank' | 'jerusalem' | 'israel';

export const SHIPPING_OPTIONS: { value: ShippingLocation; label: string; fee: number }[] = [
  { value: 'ramallah', label: 'Ramallah', fee: 15 },
  { value: 'west-bank', label: 'Other West Bank City', fee: 20 },
  { value: 'jerusalem', label: 'Jerusalem', fee: 20 },
  { value: 'israel', label: 'Israel', fee: 70 },
];

const FREE_SHIPPING_THRESHOLD = 120;
const MIN_ORDER = 80;

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  showOrderForm: boolean;
  shippingLocation: ShippingLocation | null;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'quantity'> }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'UPDATE_VERSION'; payload: { id: string; version: string; price?: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_CART'; payload?: boolean }
  | { type: 'TOGGLE_ORDER_FORM'; payload?: boolean }
  | { type: 'SET_SHIPPING_LOCATION'; payload: ShippingLocation }
  | { type: 'LOAD_CART'; payload: CartItem[] };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing = state.items.find(i => i.id === action.payload.id);
      if (existing) {
        return {
          ...state,
          items: state.items.map(i =>
            i.id === action.payload.id ? { ...i, quantity: i.quantity + 1 } : i
          ),
        };
      }
      return { ...state, items: [...state.items, { ...action.payload, quantity: 1 }] };
    }
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter(i => i.id !== action.payload) };
    case 'UPDATE_QUANTITY': {
      if (action.payload.quantity <= 0) {
        return { ...state, items: state.items.filter(i => i.id !== action.payload.id) };
      }
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.payload.id ? { ...i, quantity: action.payload.quantity } : i
        ),
      };
    }
    case 'UPDATE_VERSION': {
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.payload.id
            ? { ...i, version: action.payload.version, ...(action.payload.price != null ? { price: action.payload.price } : {}) }
            : i
        ),
      };
    }
    case 'CLEAR_CART':
      return { ...state, items: [], showOrderForm: false, shippingLocation: null };
    case 'TOGGLE_CART':
      return { ...state, isOpen: action.payload ?? !state.isOpen, showOrderForm: false };
    case 'TOGGLE_ORDER_FORM':
      return { ...state, showOrderForm: action.payload ?? !state.showOrderForm };
    case 'SET_SHIPPING_LOCATION':
      return { ...state, shippingLocation: action.payload };
    case 'LOAD_CART':
      return { ...state, items: action.payload };
    default:
      return state;
  }
}

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  showOrderForm: boolean;
  shippingLocation: ShippingLocation | null;
  totalItems: number;
  subtotal: number;
  shippingFee: number;
  total: number;
  freeShipping: boolean;
  canCheckout: boolean;
  allVersionsSelected: boolean;
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateItemVersion: (id: string, version: string, price?: number) => void;
  clearCart: () => void;
  toggleCart: (open?: boolean) => void;
  toggleOrderForm: (open?: boolean) => void;
  setShippingLocation: (loc: ShippingLocation) => void;
}

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    isOpen: false,
    showOrderForm: false,
    shippingLocation: null,
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem('dlc-cart');
      if (saved) dispatch({ type: 'LOAD_CART', payload: JSON.parse(saved) });
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem('dlc-cart', JSON.stringify(state.items));
  }, [state.items]);

  const subtotal = state.items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const freeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;
  const locationFee = state.shippingLocation
    ? SHIPPING_OPTIONS.find(o => o.value === state.shippingLocation)?.fee ?? 0
    : 0;
  const shippingFee = freeShipping ? 0 : (state.items.length > 0 ? locationFee : 0);
  const total = subtotal + shippingFee;
  const allVersionsSelected = state.items.every(i => !i.availableVersions || i.availableVersions.length <= 1 || !!i.version);
  const canCheckout = subtotal >= MIN_ORDER && state.shippingLocation !== null && allVersionsSelected;

  const value: CartContextType = {
    items: state.items,
    isOpen: state.isOpen,
    showOrderForm: state.showOrderForm,
    shippingLocation: state.shippingLocation,
    totalItems: state.items.reduce((sum, i) => sum + i.quantity, 0),
    subtotal,
    shippingFee,
    total,
    freeShipping,
    canCheckout,
    allVersionsSelected,
    addItem: (item) => {
      dispatch({ type: 'ADD_ITEM', payload: item });
      dispatch({ type: 'TOGGLE_CART', payload: true });
    },
    removeItem: (id) => dispatch({ type: 'REMOVE_ITEM', payload: id }),
    updateQuantity: (id, qty) => dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity: qty } }),
    updateItemVersion: (id, version, price) => dispatch({ type: 'UPDATE_VERSION', payload: { id, version, price } }),
    clearCart: () => dispatch({ type: 'CLEAR_CART' }),
    toggleCart: (open) => dispatch({ type: 'TOGGLE_CART', payload: open }),
    toggleOrderForm: (open) => dispatch({ type: 'TOGGLE_ORDER_FORM', payload: open }),
    setShippingLocation: (loc) => dispatch({ type: 'SET_SHIPPING_LOCATION', payload: loc }),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
